/**
 * BeziehungsnetzTab — the Kunde-Struktur interactive ego graph (Story UI-U8).
 *
 * Ported from Sebastian's kunde-struktur-tab.tsx, wired to live data
 * (GET /beziehungsnetz/:subjectType/:id) with a defensive fallback: when the
 * endpoint is absent/errors in live mode, it renders the existing flat feed as
 * the Liste view (no crash). Diagramm/Liste toggle (icon-only SegmentedControl),
 * Nur-Konzernstruktur + per-type filters, Einheit search, BFS hover path-highlight,
 * and firm-node re-center with a breadcrumb (each centre = one server read).
 *
 * Colours come from `rel-*` tokens ONLY (design.md §2/§7). No chart lib.
 *
 * [Source: brokly-design-reference/components/brokly/kunde-struktur-tab.tsx]
 */

import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  ChevronRight,
  Building2,
  UserRound,
  GitBranch,
  List,
  Search,
} from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { SegmentedControl } from '../../../../components/ui/SegmentedControl';
import { EntityLink } from '../../../../components/ui/EntityLink';
import { cn } from '../../../../lib/cn';
import { useBeziehungsnetz } from '../../hooks/useBeziehungsnetz';
import type { BeziehungsnetzSubjectType } from '../../hooks/useBeziehungsnetz';
import {
  deriveView,
  edgePoint,
  buildAdjacency,
  pathToCenter,
  neighborsOf,
  CANVAS_W,
  CANVAS_H,
  FILTER_CATEGORIES,
  KONZERN_CATEGORIES,
  type GraphNode,
  type GraphEdge,
} from './graph';
import { KATEGORIE_META } from './kategorie-meta';
import type { RelKategorie } from '@brokly/shared';

interface HistoryEntry {
  id: string;
  name: string;
  subjectType: BeziehungsnetzSubjectType;
}

export function BeziehungsnetzTab({
  subjectId,
  subjectType,
  subjectName,
  fallback,
}: {
  subjectId: string;
  subjectType: BeziehungsnetzSubjectType | undefined;
  subjectName: string;
  /** Rendered when the beziehungsnetz endpoint is absent (live-mode feature-detect). */
  fallback: React.ReactNode;
}) {
  const [history, setHistory] = useState<HistoryEntry[]>([
    { id: subjectId, name: subjectName, subjectType: subjectType ?? 'firma' },
  ]);
  const center = history[history.length - 1]!;

  const [ansicht, setAnsicht] = useState<'diagramm' | 'liste'>('diagramm');
  const [active, setActive] = useState<Set<RelKategorie>>(() => new Set(FILTER_CATEGORIES));
  const [hovered, setHovered] = useState<string | null>(null);
  const [q, setQ] = useState('');

  const { data, isError, isLoading } = useBeziehungsnetz(center.id, center.subjectType);

  const view = useMemo(
    () => (data ? deriveView(data, center.id) : { nodes: [], edges: [] }),
    [data, center.id],
  );

  // Filter (centre always visible) + Einheit search.
  const qLower = q.trim().toLowerCase();
  const nodes = useMemo(
    () =>
      view.nodes.filter(
        (n) =>
          // The centre is always visible; other nodes obey the type filter + search.
          n.category === 'diese' ||
          (active.has(n.category as RelKategorie) &&
            (qLower === '' || (n.name ?? '').toLowerCase().includes(qLower))),
      ),
    [view.nodes, active, qLower],
  );
  const visibleIds = useMemo(() => new Set(nodes.map((n) => n.id)), [nodes]);
  const edges = useMemo(
    () => view.edges.filter((e) => visibleIds.has(e.a) && visibleIds.has(e.b)),
    [view.edges, visibleIds],
  );
  const adj = useMemo(() => buildAdjacency(nodes, edges), [nodes, edges]);

  const diagramHi = useMemo(
    () => (hovered ? pathToCenter(hovered, center.id, adj) : null),
    [hovered, center.id, adj],
  );
  const listHi = useMemo(() => (hovered ? neighborsOf(hovered, adj) : null), [hovered, adj]);

  const konzernActive = FILTER_CATEGORIES.every((c) =>
    KONZERN_CATEGORIES.includes(c) ? active.has(c) : !active.has(c),
  );

  function toggle(cat: RelKategorie) {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }
  function recenter(node: GraphNode) {
    if (node.kind !== 'firma' || !node.firma_id || node.id === center.id) return;
    setHistory((h) => [...h, { id: node.firma_id!, name: node.name ?? node.id, subjectType: 'firma' }]);
    setHovered(null);
  }
  function goTo(index: number) {
    setHistory((h) => h.slice(0, index + 1));
    setHovered(null);
  }
  function back() {
    setHistory((h) => (h.length > 1 ? h.slice(0, -1) : h));
    setHovered(null);
  }

  // Feature-detect: endpoint absent/errored → flat fallback (Liste).
  if (isError) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-xs text-muted-foreground">
          Beziehungsnetz-Ansicht nicht verfügbar, zeige die Beziehungsliste.
        </p>
        {fallback}
      </div>
    );
  }
  if (isLoading && !data) {
    return <p className="p-8 text-center text-sm text-muted-foreground">Lade Beziehungsnetz …</p>;
  }

  const count = nodes.length;

  return (
    <div className="flex flex-col gap-5">
      {/* Header: title, quick filter, type toggles, view switch */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">Beziehungsnetz</h3>
          <p className="text-xs text-muted-foreground">
            {count} verknüpfte {count === 1 ? 'Einheit' : 'Einheiten'} rund um {center.name}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={konzernActive ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setActive(new Set(KONZERN_CATEGORIES))}
          >
            Nur Konzernstruktur
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setActive(new Set(FILTER_CATEGORIES))}>
            Alle Typen
          </Button>
          <SegmentedControl
            ariaLabel="Ansicht"
            iconOnly
            value={ansicht}
            onValueChange={(v) => setAnsicht(v as 'diagramm' | 'liste')}
            options={[
              { value: 'diagramm', label: 'Diagramm', icon: <GitBranch className="size-3.5" /> },
              { value: 'liste', label: 'Liste', icon: <List className="size-3.5" /> },
            ]}
          />
        </div>
      </div>

      {/* Type filter chips + search */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Einheit suchen …"
            aria-label="Einheit suchen"
            className="h-8 w-48 rounded-md border bg-background pl-8 pr-2 text-xs text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
          />
        </div>
        {FILTER_CATEGORIES.map((cat) => {
          const meta = KATEGORIE_META[cat];
          const on = active.has(cat);
          return (
            <button
              key={cat}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(cat)}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                on ? 'text-foreground' : 'text-muted-foreground opacity-60 hover:opacity-100',
              )}
            >
              <span className={cn('size-2 shrink-0 rounded-full', meta.dot)} aria-hidden />
              {meta.plural}
            </button>
          );
        })}
      </div>

      {ansicht === 'diagramm' ? (
        <DiagramView
          nodes={nodes}
          edges={edges}
          centerId={center.id}
          hovered={hovered}
          highlight={diagramHi}
          onHover={setHovered}
          onRecenter={recenter}
          history={history}
          onCrumb={goTo}
          onBack={back}
        />
      ) : (
        <ListView
          nodes={nodes}
          active={active}
          hovered={hovered}
          highlight={listHi}
          onHover={setHovered}
          kunde={center.name}
          onRecenter={recenter}
        />
      )}

      <Legend active={active} />
    </div>
  );
}

// ── Diagram ────────────────────────────────────────────────────────────────

function DiagramView({
  nodes,
  edges,
  centerId,
  hovered,
  highlight,
  onHover,
  onRecenter,
  history,
  onCrumb,
  onBack,
}: {
  nodes: GraphNode[];
  edges: GraphEdge[];
  centerId: string;
  hovered: string | null;
  highlight: { nodes: Set<string>; edges: Set<string> } | null;
  onHover: (id: string | null) => void;
  onRecenter: (n: GraphNode) => void;
  history: HistoryEntry[];
  onCrumb: (index: number) => void;
  onBack: () => void;
}) {
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const geo = useMemo(
    () =>
      edges
        .map((e) => {
          const a = byId.get(e.a);
          const b = byId.get(e.b);
          if (!a || !b) return null;
          const pa = edgePoint(a, b);
          const pb = edgePoint(b, a);
          return { e, pa, pb, mx: (pa.x + pb.x) / 2, my: (pa.y + pb.y) / 2 };
        })
        .filter((g): g is NonNullable<typeof g> => g !== null),
    [edges, byId],
  );

  return (
    <div className="overflow-x-auto rounded-lg border bg-card p-4">
      {history.length > 1 && (
        <div className="mb-3 flex items-center gap-1.5">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="size-3.5" />
            Zurück
          </Button>
          <span className="h-4 w-px bg-border" aria-hidden />
          <nav className="flex min-w-0 flex-wrap items-center gap-0.5 text-xs">
            {history.map((h, i) => {
              const last = i === history.length - 1;
              return (
                <span key={`${h.id}-${i}`} className="flex items-center gap-0.5">
                  {i > 0 && <ChevronRight className="size-3 shrink-0 text-muted-foreground" aria-hidden />}
                  {last ? (
                    <span className="max-w-[180px] truncate font-medium text-foreground">{h.name}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onCrumb(i)}
                      className="max-w-[160px] truncate rounded px-1 py-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      {h.name}
                    </button>
                  )}
                </span>
              );
            })}
          </nav>
        </div>
      )}

      <div
        className="relative mx-auto"
        style={{ width: CANVAS_W, height: CANVAS_H }}
        onMouseLeave={() => onHover(null)}
      >
        <svg className="absolute inset-0" width={CANVAS_W} height={CANVAS_H} viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}>
          {geo.map(({ e, pa, pb }) => {
            const meta = KATEGORIE_META[e.category];
            const isHot = highlight?.edges.has(e.id) ?? false;
            const dim = hovered != null && !isHot;
            return (
              <line
                key={e.id}
                x1={pa.x}
                y1={pa.y}
                x2={pb.x}
                y2={pb.y}
                className={cn(meta.stroke, 'transition-opacity', dim ? 'opacity-15' : 'opacity-100')}
                strokeWidth={isHot ? 2.5 : 1.5}
                strokeLinecap="round"
              />
            );
          })}
        </svg>

        {nodes.map((n) => {
          const isHot = highlight?.nodes.has(n.id) ?? false;
          const dim = hovered != null && !isHot;
          return (
            <NodeBox
              key={n.id}
              node={n}
              isCenter={n.id === centerId}
              dim={dim}
              onHover={onHover}
              onRecenter={onRecenter}
            />
          );
        })}

        <div className="pointer-events-none absolute inset-0 z-30">
          {geo.map(({ e, mx, my }) =>
            e.label && (hovered != null && (e.a === hovered || e.b === hovered)) ? (
              <span
                key={e.id}
                style={{ left: mx, top: my }}
                className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-md border bg-popover/95 px-1.5 py-0.5 text-[10px] font-medium text-popover-foreground shadow-sm"
              >
                {e.label}
              </span>
            ) : null,
          )}
        </div>
      </div>
    </div>
  );
}

function NodeBox({
  node,
  isCenter,
  dim,
  onHover,
  onRecenter,
}: {
  node: GraphNode;
  isCenter: boolean;
  dim: boolean;
  onHover: (id: string | null) => void;
  onRecenter: (n: GraphNode) => void;
}) {
  const isPerson = node.kind === 'person';
  const meta = isCenter ? null : KATEGORIE_META[node.category as RelKategorie];
  const canRecenter = node.kind === 'firma' && !isCenter && Boolean(node.firma_id);
  const hasSubline = isPerson || Boolean(node.uid || node.kanton);

  return (
    <button
      type="button"
      style={{ left: node.x, top: node.y }}
      onMouseEnter={() => onHover(node.id)}
      onFocus={() => onHover(node.id)}
      onClick={() => canRecenter && onRecenter(node)}
      title={canRecenter ? `${node.name} ins Zentrum rücken` : node.name ?? undefined}
      className={cn(
        'group absolute z-10 -translate-x-1/2 -translate-y-1/2 outline-none transition-opacity',
        canRecenter ? 'cursor-pointer' : 'cursor-default',
        dim ? 'opacity-40' : 'opacity-100',
      )}
    >
      <div
        className={cn(
          'flex w-[156px] items-center gap-2 border bg-card px-2.5 py-2 text-left shadow-sm transition-colors',
          isPerson ? 'rounded-full' : 'rounded-lg',
          isCenter ? 'border-sem-info bg-sem-info-bg' : 'group-hover:border-foreground/30',
        )}
      >
        <span
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground',
            isPerson && 'rounded-full',
          )}
          aria-hidden
        >
          {isPerson ? <UserRound className="size-4" /> : <Building2 className="size-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span
              className={cn('size-1.5 shrink-0 rounded-full', isCenter ? 'bg-sem-info' : meta!.dot)}
              aria-hidden
            />
            <span className="truncate text-xs font-medium text-foreground">{node.name}</span>
          </div>
          {hasSubline && (
            <p className="truncate pl-3 text-[11px] text-muted-foreground">
              <EntitySubline node={node} />
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

// ── Liste ──────────────────────────────────────────────────────────────────

function ListView({
  nodes,
  active,
  hovered,
  highlight,
  onHover,
  kunde,
  onRecenter,
}: {
  nodes: GraphNode[];
  active: Set<RelKategorie>;
  hovered: string | null;
  highlight: Set<string> | null;
  onHover: (id: string | null) => void;
  kunde: string;
  onRecenter: (n: GraphNode) => void;
}) {
  const anchor = nodes.find((n) => n.category === 'diese');
  const groups = FILTER_CATEGORIES.filter((c) => active.has(c))
    .map((cat) => ({ cat, items: nodes.filter((n) => n.category === cat) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="flex flex-col gap-6" onMouseLeave={() => onHover(null)}>
      {anchor && (
        <section className="flex flex-col gap-2">
          <GroupHeading dot="bg-sem-info" Icon={Building2} label="Diese Gesellschaft" />
          <RelationRow node={anchor} emphasized related={false} onHover={onHover} onRecenter={onRecenter} />
        </section>
      )}
      {groups.map(({ cat, items }) => {
        const meta = KATEGORIE_META[cat];
        return (
          <section key={cat} className="flex flex-col gap-2">
            <GroupHeading dot={meta.dot} Icon={meta.Icon} label={meta.plural} hint={meta.hint(kunde)} count={items.length} />
            <div className="flex flex-col gap-2">
              {items.map((n) => (
                <RelationRow
                  key={n.id}
                  node={n}
                  emphasized={false}
                  related={highlight != null && hovered !== n.id && highlight.has(n.id)}
                  onHover={onHover}
                  onRecenter={onRecenter}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function GroupHeading({
  dot,
  Icon,
  label,
  hint,
  count,
}: {
  dot: string;
  Icon: typeof Building2;
  label: string;
  hint?: string;
  count?: number;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span className={cn('size-2 shrink-0 translate-y-0.5 rounded-full', dot)} aria-hidden />
      <Icon className="size-3.5 shrink-0 translate-y-0.5 text-muted-foreground" />
      <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground">
        {label}
        {typeof count === 'number' && (
          <span className="ml-1.5 font-normal tabular-nums text-muted-foreground">{count}</span>
        )}
      </h4>
      {hint && <span className="text-xs text-muted-foreground">· {hint}</span>}
    </div>
  );
}

function RelationRow({
  node,
  emphasized,
  related,
  onHover,
  onRecenter,
}: {
  node: GraphNode;
  emphasized: boolean;
  related: boolean;
  onHover: (id: string | null) => void;
  onRecenter: (n: GraphNode) => void;
}) {
  const isPerson = node.kind === 'person';
  const canRecenter = node.kind === 'firma' && !emphasized && Boolean(node.firma_id);
  return (
    <div
      onMouseEnter={() => onHover(node.id)}
      className={cn(
        'flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors',
        emphasized ? 'border-sem-info bg-sem-info-bg' : related ? 'border-sem-info/50 bg-muted/40' : 'bg-card hover:border-foreground/20',
      )}
    >
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground',
          isPerson && 'rounded-full',
        )}
        aria-hidden
      >
        {isPerson ? <UserRound className="size-4" /> : <Building2 className="size-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {emphasized || !canRecenter ? (
            <span className="truncate text-sm font-medium text-foreground">{node.name}</span>
          ) : (
            <EntityLink onActivate={() => onRecenter(node)}>
              <span className="truncate">{node.name}</span>
            </EntityLink>
          )}
          {emphasized && (
            <span className="rounded-full border border-sem-info/40 px-1.5 py-px text-[10px] font-medium text-sem-info">
              Ausgangspunkt
            </span>
          )}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          <EntitySubline node={node} />
        </p>
      </div>
    </div>
  );
}

/**
 * Firma → UID (mono) · Kanton; Person → "natürliche Person".
 * The company UID is an ID → `mono-num` (design.md §3/§7; AC9), matching every
 * sibling detail screen. Kanton stays in the body sans font.
 */
function EntitySubline({ node }: { node: GraphNode }) {
  if (node.kind === 'person') return <>natürliche Person</>;
  return (
    <>
      {node.uid && <span className="mono-num">{node.uid}</span>}
      {node.uid && node.kanton ? ' · ' : null}
      {node.kanton}
    </>
  );
}

// ── Legend ─────────────────────────────────────────────────────────────────

function Legend({ active }: { active: Set<RelKategorie> }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-sem-info" aria-hidden />
          <span className="text-xs text-foreground">Diese Gesellschaft</span>
        </span>
        {FILTER_CATEGORIES.map((cat) => {
          const meta = KATEGORIE_META[cat];
          const on = active.has(cat);
          return (
            <span
              key={cat}
              className={cn('flex items-center gap-1.5 transition-opacity', on ? 'opacity-100' : 'opacity-35')}
            >
              <span className={cn('size-2.5 rounded-full', meta.dot)} aria-hidden />
              <meta.Icon className="size-3.5 text-muted-foreground" />
              <span className="text-xs text-foreground">{meta.plural}</span>
            </span>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        Beim Überfahren eines Knotens wird der Pfad zur betrachteten Gesellschaft betont und die
        Beziehungsbezeichnung eingeblendet. Ein Klick auf eine Gesellschaft rückt sie ins Zentrum.
      </p>
    </div>
  );
}
